import React, { useCallback, useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";
import DialogBox from "@/components/Common/DialogBox";
import Loader from "@/components/Loader";
import MarketingCampaignCard from "@/components/marketing/MarketingCampaignCard";
import MarketingCampaignModal from "@/components/marketing/MarketingCampaignModal";
import { marketingCampaignService } from "@/services/marketingCampaignService";
import useToastStore from "@/components/Common/Toast/toastStore";
import { useDebounce } from "@/utils/helpers";

const normalizeCampaign = (item) => ({
  id: item.id,
  name: item.name ?? item.title ?? "",
  channel: item.channel || "email",
  subject: item.subject ?? "",
  email_body: item.email_body ?? item.body ?? item.content ?? "",
  sms_body: item.sms_body ?? "",
  created_at: item.created_at,
  updated_at: item.updated_at,
});

const MarketingCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [viewCampaign, setViewCampaign] = useState(null);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const { showToast } = useToastStore;

  const loadCampaigns = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = { per_page: 100 };
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await marketingCampaignService.getCampaigns(params);

      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.templates)
          ? response.templates
          : Array.isArray(response?.campaigns)
            ? response.campaigns
            : Array.isArray(response)
              ? response
              : [];

      setCampaigns(list.map(normalizeCampaign));
    } catch (error) {
      showToast({
        message: error.message || "Failed to fetch templates",
        type: "error",
      });
      setCampaigns([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, showToast]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const updateSearchTerm = (term) => {
    setSearchTerm(term);
  };

  const debouncedSearch = useDebounce(updateSearchTerm, 500);

  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  const handleOpenAdd = () => {
    setSelectedCampaign(null);
    setIsFormModalOpen(true);
  };

  const handleOpenEdit = (campaign) => {
    setSelectedCampaign(campaign);
    setIsFormModalOpen(true);
  };

  const handleOpenView = (campaign) => {
    setViewCampaign(campaign);
    setShowViewModal(true);
  };

  const handleDeleteClick = (campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!campaignToDelete) return;

    try {
      setIsDeleting(true);
      await marketingCampaignService.deleteCampaign(campaignToDelete.id);
      showToast({ message: "Template deleted successfully", type: "success" });
      setShowDeleteModal(false);
      setCampaignToDelete(null);
      await loadCampaigns();
    } catch (error) {
      showToast({
        message: error.message || "Failed to delete template",
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const closeFormModal = () => {
    setIsFormModalOpen(false);
    setSelectedCampaign(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex w-full flex-wrap items-center gap-3">
        <div className="w-full sm:max-w-[384px] shrink-0">
          <div className="self-stretch h-11 px-3.5 py-2.5 bg-background rounded-lg border border-input inline-flex justify-start items-center gap-2 overflow-hidden w-full">
            <Search className="w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search templates"
              value={tempSearchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="flex-1 text-foreground placeholder:text-muted-foreground text-base font-normal leading-6 outline-none bg-transparent"
            />
          </div>
        </div>

        <div className="ml-auto flex shrink-0 items-center">
          <button
            type="button"
            onClick={handleOpenAdd}
            className="bg-primary-dark text-white px-4 h-11 rounded-lg shadow-sm font-semibold transition-all hover:bg-slate-900 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Templates
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Loader bgColor="bg-transparent" loadingText="Loading templates..." />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <h3 className="text-lg font-medium text-foreground">
            No Templates Found
          </h3>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {campaigns.map((campaign) => (
            <MarketingCampaignCard
              key={campaign.id}
              campaign={campaign}
              onEdit={handleOpenEdit}
              onDelete={handleDeleteClick}
              onView={handleOpenView}
            />
          ))}
        </div>
      )}

      <MarketingCampaignModal
        isOpen={isFormModalOpen}
        onClose={closeFormModal}
        campaign={selectedCampaign}
        onSaved={loadCampaigns}
      />

      <DialogBox
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewCampaign(null);
        }}
        title="Template Details"
        className="w-[45%] max-md:w-[90%]"
        showIcon={false}
      >
        <div>
          <div className="py-3">
            <p className="text-lg font-semibold text-primary-dark break-words leading-7">
              {viewCampaign?.name || "-"}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Channel:{" "}
              {viewCampaign?.channel === "both"
                ? "Email & SMS"
                : viewCampaign?.channel === "sms"
                  ? "SMS"
                  : "Email"}
            </p>
            {viewCampaign?.channel !== "sms" && viewCampaign?.subject && (
              <p className="text-sm text-muted-foreground mt-1 break-words">
                Subject: {viewCampaign.subject}
              </p>
            )}
          </div>

          {viewCampaign?.email_body && (
            <div className="rounded-xl border border-border bg-muted px-4 py-4 mb-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Email</p>
              <div className="max-h-[30vh] overflow-y-auto pr-1 prose prose-sm max-w-none">
                <div
                  dangerouslySetInnerHTML={{ __html: viewCampaign.email_body }}
                />
              </div>
            </div>
          )}

          {viewCampaign?.sms_body && (
            <div className="rounded-xl border border-border bg-muted px-4 py-4">
              <p className="text-xs font-medium text-muted-foreground mb-2">SMS</p>
              <div className="max-h-[30vh] overflow-y-auto pr-1 prose prose-sm max-w-none">
                <div
                  dangerouslySetInnerHTML={{ __html: viewCampaign.sms_body }}
                />
              </div>
            </div>
          )}

          {!viewCampaign?.email_body && !viewCampaign?.sms_body && (
            <div className="rounded-xl border border-border bg-muted px-4 py-4">
              <p className="text-base text-muted-foreground">-</p>
            </div>
          )}
        </div>
      </DialogBox>

      <DialogBox
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCampaignToDelete(null);
        }}
        title="Delete Template"
        className="w-[40%] max-md:w-[90%]"
        actionButton={
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto disabled:opacity-50"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        }
      >
        <p className="text-base text-muted-foreground">
          Are you sure you want to delete &quot;{campaignToDelete?.name}&quot;?
          This action cannot be undone.
        </p>
      </DialogBox>
    </div>
  );
};

export default MarketingCampaigns;
